import { type ComponentProps, forwardRef, type Ref, useEffect, useRef, useState } from 'react';

import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';

import { Input, Select, useDebouncedCallback } from '@wf/components';

import OptionWithTwoDescriptions from '../../../../components/OptionWithTwoDescriptions';

import { type IAdditionalFieldConfig } from '../../types/response';
import useMenuPlacement from '../../../../hooks/useMenuPlacement';

import { getAdditionalFieldOptions, getDbOptionById } from '../../services/lpco';

export type IAdditionalFieldSelectProps = ComponentProps<typeof Select> & {
  minSuggest?: number;
  config: IAdditionalFieldConfig;
  maxFieldUILength: number;
  sectionClassName?: string;
};

function AdditionalFieldSelect(
  {
    label,
    onInputChange,
    minSuggest = 2,
    className,
    sectionClassName,
    maxFieldUILength = 3,
    value,
    viewMode,
    name,
    config: {
      dbOptionTypeId,
      name2Label,
      name2NationalLanguageLabel,
      name3Label,
      name3NationalLanguageLabel,
      field2UILength,
      field3UILength,
    },
    ...props
  }: IAdditionalFieldSelectProps,
  ref: Ref<HTMLSelectElement>
) {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const { containerRef, menuPlacement } = useMenuPlacement();

  const { setValue } = useFormContext();

  const [searchText, setSearchText] = useState('');
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState<Array<SelectOption>>([]);

  const field2Label = language === 'en' ? name2Label : name2NationalLanguageLabel || name2Label;
  const field3Label = language === 'en' ? name3Label : name3NationalLanguageLabel || name3Label;

  const field2UILengthClassName =
    typeof field2UILength === 'number'
      ? `grid-columns-size-${field2UILength <= maxFieldUILength ? field2UILength : maxFieldUILength}`
      : '';
  const field3UILengthClassName =
    typeof field3UILength === 'number'
      ? `grid-columns-size-${field3UILength <= maxFieldUILength ? field3UILength : maxFieldUILength}`
      : '';

  const abortController = useRef<AbortController>();

  const handleDebounceSearch = useDebouncedCallback(
    (searchText: string) => {
      abortController.current?.abort();

      if (searchText.length < minSuggest) {
        setOptions([]);

        return;
      }

      abortController.current = new AbortController();
      const signal = abortController.current?.signal;

      getAdditionalFieldOptions({
        optionTypeId: dbOptionTypeId as number,
        searchText,
        signal,
      })
        .then(({ data }) => {
          setOptions(
            data.map(({ id, code, name, name2 }) => ({
              value: String(id),
              label: code,
              name,
              name2,
            }))
          );
        })
        .catch((error) => {
          console.error(error);
        })
        .finally(() => {
          if (!signal?.aborted) {
            setLoading(false);
          }
        });
    },
    500,
    [minSuggest, label]
  );

  useEffect(() => {
    if (minSuggest === 0) {
      setLoading(true);
      handleDebounceSearch('');
    }
  }, []);

  const handleSearch = (searchText: string) => {
    setLoading(searchText.length >= minSuggest);

    handleDebounceSearch(searchText);
  };

  useEffect(() => {
    // During import from Excel file only optionId is imported
    // so need to get other values from backend.
    // May be changed later and set during import process when
    // backend API is ready
    if (value && !value.label) {
      getDbOptionById({
        typeId: dbOptionTypeId as number,
        id: value.value,
      })
        .then((res) => {
          const option = res.data.resultList[0];

          if (option) {
            const { id, code, name: name1, name2 } = option;
            setValue(name as string, {
              value: id,
              label: code,
              name: name1,
              name2,
            });
          }
        })
        .catch((e) => console.error(e));
    }
  }, []);

  const noOptionMessage = () =>
    searchText.length < minSuggest
      ? t('Please type at least {{minSuggest}} characters to see options', { minSuggest })
      : t('No options');

  return (
    <>
      <div className={cn(className, sectionClassName)} ref={containerRef}>
        <Select
          {...props}
          onInputChange={(value, actionMeta) => {
            if (value !== searchText) {
              setSearchText(value);
              onInputChange?.(value, actionMeta);
              handleSearch(value);
            }
          }}
          placeholder={minSuggest ? `${t('Search')} (${t('Min')} ${minSuggest})` : t('Search')}
          // @ts-expect-error
          components={{ Option: (props) => <OptionWithTwoDescriptions {...props} /> }}
          options={minSuggest && searchText.length < minSuggest ? [] : options}
          noOptionsMessage={noOptionMessage}
          menuPlacement={menuPlacement}
          filterOption={() => true}
          viewMode={viewMode}
          isLoading={loading}
          value={value}
          label={label}
          name={name}
          ref={ref}
          isClearable
        />
      </div>
      {name2Label && (
        <Input
          className={cn(field2UILengthClassName, sectionClassName)}
          valueView={value?.name || '-'}
          value={value?.name || ''}
          label={field2Label}
          viewMode={viewMode}
          textarea={viewMode}
          disabled
        />
      )}
      {name3Label && (
        <Input
          className={cn(field3UILengthClassName, sectionClassName)}
          valueView={value?.name2 || '-'}
          value={value?.name2 || ''}
          label={field3Label}
          viewMode={viewMode}
          textarea={viewMode}
          disabled
        />
      )}
    </>
  );
}

export default forwardRef(AdditionalFieldSelect);
